/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace component-selector */

import {
  Component,
  OnInit,
  OnDestroy
}                       from '@angular/core';

import { Subscription } from 'rxjs';

import { AlertService } from '../services/alert.service';

import {
  Router,
  NavigationStart
}                       from '@angular/router';

import {
  Alert,
  AlertType
}                       from '../models/alert';

@Component({
  selector:    'alert',
  templateUrl: 'alert.component.html'
})

/**
 *
 * @class AlertComponent
 *
 * @brief a list of system wide messages at the top of the application which "pop" (like a toaster).
 * Messages can be dismissed, auto-close/auto-fade, or be persistent across navigation/page changes.
 *
 * Angular tutorial on toaster style alerts
 * @link https://jasonwatmore.com/post/2019/07/05/angular-8-alert-toaster-notifications
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class AlertComponent implements OnInit, OnDestroy {

  /** the observable for alerts we are interested in */
  private subscription:      Subscription;

  /** the observable for router events (navigation/page changes) */
  private routeSubscription: Subscription;

  /** the stream of alerts we subscribe to */
  public  id:                string = 'default-alert';

  /** when dismissed, just remove, or fade out? */
  public  fade:              boolean = true;

  /** the current alert message */

  alerts: Alert[] = [];

  constructor(
    private router: Router,
    private alertService: AlertService
  ) {

  }

  /* on creation subscribe to the alert service so we get updates when alerts happen from anywhere */
  ngOnInit() {

    /* subscribe to new alert notifications */

    this.subscription = this.alertService.onAlert(this.id)
      .subscribe(alert => {

        /* clear alerts when an empty alert is received */

        if (!alert.message) {

          /* filter out alerts without 'keepAfterRouteChange' flag */

          this.alerts = this.alerts.filter(x => x.keepAfterRouteChange);

          /* remove 'keepAfterRouteChange' flag on the rest */

          this.alerts.forEach(x => delete x.keepAfterRouteChange);

          return;
        }

        /* pop the alert */

        this.alerts.push(alert);

        /* auto close alert if required */

        if (alert.autoClose) {

          setTimeout(() => this.removeAlert(alert), 3000);
        }
      });

    /*
     * clear alerts on location change; combined with ^^^ above subscribe code,
     * the effect is that even if you persist the message it won't post forever,
     * after a page change, it will un-persist, so that after that it goes away...
     * persist is only for one page change at most.  No zombies!
     *
     */

    this.routeSubscription = this.router.events.subscribe(event => {

      if (event instanceof NavigationStart) {
        this.alertService.clear(this.id);
      }
    });
  }

  /** dismiss an alert */
  removeAlert(alert: Alert) {

    /* is it already  gone? */

    if (!this.alerts.includes(alert)) {

      return;
    }

    if (this.fade) {

      /* add the fade out class to that alert (triggers CSS transitions) */

      this.alerts.find(x => x === alert).fade = true;

      /* remove alert after faded out */

      setTimeout(() => {

        this.alerts = this.alerts.filter(x => x !== alert);

      }, 500);

    } else {

      /* just remove it */

      this.alerts = this.alerts.filter(x => x !== alert);
    }
  }

  /* clean up at the end */
  ngOnDestroy() {

    this.subscription.unsubscribe();

    this.routeSubscription.unsubscribe();
  }

  /**
   * when the alerts change, the template will re-render the list, and
   * the css classes will be updated.  So if fade is on and you manually
   * click to remove, you still get fade out, even if the alert wasn't
   * configured with fade out.
   *
   */
  cssClass(alert: Alert) {

    if (!alert) {

      return;
    }

    const classes = ['system-alert', 'alert', 'alert-dismissable'];

    const alertTypeClass = {
      [AlertType.Success]: 'alert alert-success',
      [AlertType.Error]: 'alert alert-danger',
      [AlertType.Info]: 'alert alert-info',
      [AlertType.Warning]: 'alert alert-warning'
    };

    classes.push(alertTypeClass[alert.type]);

    if (alert.fade) {

      classes.push('system-alert-fade');
    }

    return classes.join(' ');
  }
}
